import React, { ReactNode } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import BaseModal, { type BaseModalFooterAction } from './BaseModal'
import { buildKiotModalFooterActions, kiotButtonCancelSx } from '../button'

interface BaseModalImportProps {
  open: boolean
  onClose: () => void
  onSave: () => void
  loading?: boolean
  disabled?: boolean
  title?: string
  /** Mô tả ngắn, ví dụ: "Chọn file XLSX (định dạng theo file mẫu)." */
  description?: ReactNode
  fileName?: string
  onChooseFile?: () => void
  children?: ReactNode
  templateComponent?: ReactNode
  closeLabel?: string
  saveLabel?: string
  chooseFileLabel?: string
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  /** Ghi đè footer mặc định (Đóng + Import) */
  footerActions?: BaseModalFooterAction[]
  /** Ẩn form chọn file — chỉ hiển thị kết quả / children */
  hideFilePicker?: boolean
  /** Dòng trạng thái hiển thị dưới spinner khi `loading` */
  loadingText?: ReactNode
}

const BaseModalImport: React.FC<BaseModalImportProps> = ({
  open,
  onClose,
  onSave,
  loading = false,
  disabled = false,
  title = 'Import',
  description,
  fileName = '',
  onChooseFile,
  children,
  templateComponent,
  closeLabel = 'Đóng',
  saveLabel = 'Import',
  chooseFileLabel = 'Chọn file',
  maxWidth = 'sm',
  footerActions: footerActionsOverride,
  hideFilePicker = false,
  loadingText,
}) => {
  const defaultFooterActions = buildKiotModalFooterActions({
    cancelLabel: closeLabel,
    saveLabel,
    onCancel: onClose,
    onSave,
    cancelDisabled: loading,
    saveDisabled: disabled || loading,
    saveStartIcon: <FileUploadIcon />,
  })

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      title={title}
      closeButtonAriaLabel={closeLabel}
      paperSx={{ minHeight: hideFilePicker ? 280 : 220 }}
      footerActions={footerActionsOverride ?? defaultFooterActions}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1.5,
            minHeight: 120,
          }}
        >
          <CircularProgress size={36} />
          {loadingText ? (
            <Typography variant="body2" color="text.secondary">
              {loadingText}
            </Typography>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!hideFilePicker ? (
            <>
              {description && (
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<CloudUploadIcon />}
                  sx={kiotButtonCancelSx}
                  onClick={onChooseFile}
                  aria-label={chooseFileLabel}
                >
                  {chooseFileLabel}
                </Button>
                {templateComponent}
              </Box>
              {fileName && (
                <Typography variant="body2" color="text.secondary">
                  Đã chọn: {fileName}
                </Typography>
              )}
            </>
          ) : null}
          {children}
        </Box>
      )}
    </BaseModal>
  )
}

export default BaseModalImport
